package games.polyclub.website.frontend.store.pages.logged

import games.polyclub.frontend.lib.messagerenderer.Viewer
import games.polyclub.frontend.lib.messagerenderer.m
import games.polyclub.frontend.lib.react.bootstrap.components.BAlert
import games.polyclub.frontend.lib.react.bootstrap.components.Card
import games.polyclub.frontend.lib.react.bootstrap.css.utility.BootstrapColor
import games.polyclub.frontend.lib.react.bootstrap.custom.oldcomponents.CountdownProgressBar
import games.polyclub.frontend.lib.react.simplified.SFC
import games.polyclub.frontend.lib.react.simplified.tags.*
import games.polyclub.frontend.lib.react.utils.classes
import games.polyclub.frontend.lib.react.utils.useEffectOnceAsync
import games.polyclub.message.div
import games.polyclub.message.unaryMinus
import games.polyclub.service.payment.api.PaymentStatus
import games.polyclub.service.payment.api.id.Gateway
import games.polyclub.service.payment.api.id.OrderId
import games.polyclub.service.store.api.StoreOpenPayment
import games.polyclub.tool.functional.Either
import games.polyclub.tool.functional.repeatingForever
import games.polyclub.tool.functional.retrying
import games.polyclub.tool.strings.id.UniqueId.Companion.parseUniqueId
import games.polyclub.website.frontend.store.StoreBff
import games.polyclub.website.frontend.store.appScope
import games.polyclub.website.frontend.store.components.Loading
import games.polyclub.website.frontend.store.context.addBreadcrumb
import games.polyclub.website.frontend.store.layout.userarea.InvoiceDetailsComponent
import games.polyclub.website.frontend.store.layout.userarea.UserAreaLayout
import games.polyclub.website.frontend.store.pages.MissingPage
import games.polyclub.website.frontend.store.sitemap.LoggedSitemap
import js.core.jso
import kotlinx.browser.document
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlinx.datetime.Clock
import react.Props
import react.dom.aria.AriaRole
import react.router.useNavigate
import react.router.useParams
import react.useState
import web.cssom.*
import web.html.HTMLButtonElement
import web.window.WindowTarget
import kotlin.time.Duration.Companion.minutes
import kotlin.time.Duration.Companion.seconds

val InvoicePage = SFC<Props> { _ ->
    val params = useParams()
    val orderId = try {
        OrderId(params["orderId"]!!.parseUniqueId())
    } catch (e: Exception) {
        e.printStackTrace()
        MissingPage()
        return@SFC
    }

    val navigate = useNavigate()

    val (response, setOpenPayment) = useState<Either<StoreOpenPayment, String>?>(null)
    val (now, setNow) = useState(Clock.System.now())

    appScope.useEffectOnceAsync {
        repeatingForever {
            retrying {
                setOpenPayment(StoreBff.readInvoice(orderId))
            }
            delay(5.seconds)
        }
    }

    appScope.useEffectOnceAsync {
        repeatingForever {
            delay(0.5.seconds)
            setNow(Clock.System.now())
        }
    }

    UserAreaLayout {
        pageTitle = -"Pagamento"
        pageTitleIcon = LoggedSitemap.PAY.icon

        addBreadcrumb(LoggedSitemap.ORDERS.pathPart, -"Pedidos")
        addBreadcrumb(LoggedSitemap.PAY.pathPart, -"Pagamento")

        if (response == null) {
            div("card-body") {
                Loading()
            }
        } else if (response.hasB) {
            val errorMessage = response.requireB
            BAlert {
                color = BootstrapColor.DANGER
                isDismissible = false
                +errorMessage
            }
        } else if (response.hasA) {
            val openPayment = response.requireA
            val createdAt = openPayment.createdAt
            val gateway = openPayment.gateway
            val preferredGateway = openPayment.preferredGateway
            div("row text-justify") {
                div("col-lg-5 text-center") {
                    Card {
                        val expires = sequenceOf(openPayment.expiresAt, openPayment.qrCodes.firstOrNull()?.expirationTime)
                            .filterNotNull().minOrNull()
                        val remaining = expires?.let { it - now }
                        val status = if (openPayment.status == PaymentStatus.AWAITING_PAYMENT && remaining != null && remaining <= 0.seconds) {
                            PaymentStatus.PAYMENT_EXPIRED
                        } else {
                            openPayment.status
                        }
                        when (status) {
                            PaymentStatus.CREATING -> {
                                title = "Criando pedido..."
                                p { +"Por favor, aguarde..." }
                                p { +"Aguardando o processamento terminar de preparar tudo..." }
                                div("spinner-border text-primary") {
                                    role = AriaRole.status
                                    style = jso {
                                        display = Display.block
                                        margin = Margin(0.px, Auto.auto)
                                        width = 10.rem
                                        height = 10.rem
                                    }
                                    span("sr-only")
                                }
                                p {
                                    small {
                                        em {
                                            +"Pedido: $orderId"
                                        }
                                    }
                                }
                            }
                            PaymentStatus.PROCESSING, PaymentStatus.IN_ANALYSIS, PaymentStatus.DISPUTE -> {
                                title = "Aguardando pagamento..."
                                p { +"Por favor, aguarde..." }
                                p { +"Aguardando o processamento do mediador de pagamentos..." }
                                div("spinner-border text-primary") {
                                    role = AriaRole.status
                                    style = jso {
                                        display = Display.block
                                        margin = Margin(0.px, Auto.auto)
                                        width = 10.rem
                                        height = 10.rem
                                    }
                                    span("sr-only")
                                }
                                p { +"Sair da página não irá interferir no resultado." }
                                p { m(-"Você será ${"notificada"/Viewer} por e-mail se tudo der certo.") }
                                p {
                                    small {
                                        em {
                                            +"Pedido: $orderId"
                                        }
                                    }
                                }
                            }

                            PaymentStatus.PAYMENT_EXPIRED -> {
                                title = "Expirado"
                                p { +"Ah não! O tempo para pagamento expirou. Se houve alguma cobrança ela deverá ser estornada automaticamente." }
                                p {
                                    small {
                                        em {
                                            +"Pedido: $orderId"
                                        }
                                    }
                                }
                            }

                            PaymentStatus.FULLY_REFUNDED, PaymentStatus.PARTIALLY_REFUNDED -> {
                                title = "Pagamento Devolvido"
                                p { +"Ah não! Pelo visto algum processador de pagamentos intermediário cancelou o pagamento ou o código Pix expirou. Se houve alguma cobrança ela deverá ser estornada automaticamente." }
                                p {
                                    small {
                                        em {
                                            +"Pedido: $orderId"
                                        }
                                    }
                                }
                            }

                            PaymentStatus.PAID, PaymentStatus.RELEASED -> {
                                title = "Pagamento Aprovado"
                                p { +"Parabéns!" }
                                p { +"O pagamento foi registrado e você receberá informações sobre sua compra em breve por e-mail." }
                                /*p {
                                    +"Você já pode jogar no servidor, veja como jogar "
                                    Link {
                                        to = "/"
                                        +"clicando aqui"
                                    }
                                    activationData?.playerNick?.let { nick ->
                                        +", lembre-se de logar com a conta de les personagem "
                                        ReactHTML.strong { +nick }
                                        +" para poder entrar no servidor"
                                    }
                                    +"."
                                }*/
                                p {
                                    small {
                                        em {
                                            +"Pedido: $orderId"
                                        }
                                    }
                                }
                            }

                            PaymentStatus.PAYMENT_DECLINED -> {
                                title = "Pagamento Recusado"
                                p { +"Ah não! Pelo visto algum processador de pagamentos intermediário recusou o pagamento. Se houve alguma cobrança ela deverá ser estornada automaticamente." }
                                p {
                                    small {
                                        em {
                                            +"Pedido: $orderId"
                                        }
                                    }
                                }
                            }

                            PaymentStatus.PAYMENT_CANCELED, PaymentStatus.FAILED -> {
                                title = "Pagamento Cancelado"
                                p { +"Ah não! Pelo visto algum processador de pagamentos intermediário cancelou o pagamento ou o código Pix expirou. Se houve alguma cobrança ela deverá ser estornada automaticamente." }
                                p {
                                    small {
                                        em {
                                            +"Pedido: $orderId"
                                        }
                                    }
                                }
                            }

                            PaymentStatus.AWAITING_PAYMENT -> {
                                if (remaining != null && remaining < 3.minutes) {
                                    title = "Aguardando pagamento..."
                                    p { +"O tempo de pagamento expirou." }
                                    p { +"Aguardando mais um pouco, caso o processador de pagamentos esteja atrasando a resposta." }
                                    div("spinner-border text-primary") {
                                        role = AriaRole.status
                                        style = jso {
                                            display = Display.block
                                            margin = Margin(0.px, Auto.auto)
                                            width = 10.rem
                                            height = 10.rem
                                        }
                                        span("sr-only")
                                    }
                                    CountdownProgressBar::class {
                                        this.classes = "my-4"
                                        this.min = expires - 3.minutes - 3.seconds
                                        this.max = expires
                                        showClock = true
                                    }
                                    p { +"Se você já pagou, por favor aguarde mais um pouco, caso contrário, por favor tente novamente." }
                                    p("text-center") {
                                        button("btn btn-primary") {
                                            role = AriaRole.button
                                            onClick = {
                                                navigate(LoggedSitemap.CART.path)
                                            }
                                            +"Voltar ao carrinho e tentar de novo"
                                        }
                                    }
                                    p {
                                        small {
                                            em {
                                                +"Pedido: $orderId"
                                            }
                                        }
                                    }
                                } else {
                                    title = if (gateway == Gateway.PICPAY) "Pague com PicPay" else "Conclua o Pagamento"
                                    if (gateway != preferredGateway) {
                                        div("alert alert-warning") {
                                            role = AriaRole.alert
                                            +"Devido a um problema de conexão com o ${preferredGateway.readableName}, usamos o ${gateway.readableName} para continuar com o seu pagamento."
                                        }
                                    }
                                    if (openPayment.qrCodes.isNotEmpty()) {
                                        val qrCode = openPayment.qrCodes.first()
                                        p {
                                            if (gateway == Gateway.PICPAY) {
                                                +"Abra o PicPay em seu telefone e escaneie o código abaixo:"
                                            } else if (qrCode.imageUrl != null || qrCode.imageData != null) {
                                                +"Use o QR-Code abaixo para concluir o pagamento:"
                                            } else {
                                                +"Utilize as informações abaixo para concluir o pagamento:"
                                            }
                                        }
                                        (qrCode.imageUrl ?: qrCode.imageData)?.toString()?.let { imageUrl ->
                                            p {
                                                img("mw-100") {
                                                    src = imageUrl
                                                    alt = "QR-Code"
                                                }
                                            }
                                        }
                                        qrCode.expirationTime?.let { expiresAt ->
                                            CountdownProgressBar::class {
                                                this.classes = "my-4"
                                                this.min = createdAt
                                                this.max = expiresAt - 3.minutes - 3.seconds
                                                showClock = true
                                            }
                                        }
                                        (openPayment.paymentUrl ?: qrCode.paymentUrl)?.let { externalUrl ->
                                            p {
                                                a("btn btn-primary") {
                                                    href = externalUrl.toString()
                                                    target = WindowTarget._blank
                                                    role = AriaRole.button
                                                    +"Pagar no site do ${gateway.readableName}"
                                                }
                                            }
                                        }
                                        if (gateway == Gateway.PICPAY) {
                                            p {
                                                strong {
                                                    +"Se tiver algum problema com a leitura do QR code, atualize o aplicativo."
                                                }
                                            }
                                            p {
                                                a {
                                                    href =
                                                        "https://meajuda.picpay.com/hc/pt-br/articles/360045117912-Quero-fazer-a-adi%C3%A7%C3%A3o-mas-a-op%C3%A7%C3%A3o-n%C3%A3o-aparece-para-mim-E-agora-"
                                                    target = WindowTarget._blank
                                                    +"Saiba como atualizar o aplicativo"
                                                }
                                            }
                                            h5 {
                                                a {
                                                    href = "https://picpay.com/baixar-o-app-picpay"
                                                    target = WindowTarget._blank
                                                    +"Não tem conta no PicPay? Baixe o app gratuitamente"
                                                }
                                            }
                                        }
                                        qrCode.content?.let { copyPaste ->
                                            p {
                                                +"Ou utilize código Copia-Cola abaixo:"
                                            }
                                            p("row") {
                                                textarea("col-11 col-lg-7 m-auto form-control") {
                                                    readOnly = true
                                                    style = jso {
                                                        resize = None.none
                                                        width = 100.pct
                                                        paddingBottom = 25.px
                                                        paddingTop = 10.px
                                                        display = Display.block
                                                    }
                                                    onClick = { ev ->
                                                        ev.currentTarget.select()
                                                        document.execCommand("copy")
                                                    }
                                                    +copyPaste
                                                }
                                            }
                                        }
                                        if (openPayment.isSandbox) {
                                            p("row") {
                                                button("btn btn-warning m-auto") {
                                                    role = AriaRole.button
                                                    onClick = { ev ->
                                                        val target = ev.target as HTMLButtonElement
                                                        target.disabled = true
                                                        target.innerText = "Processando..."
                                                        appScope.launch {
                                                            try {
                                                                check(StoreBff.sandboxSimulatePayment(orderId)) {
                                                                    "Falha ao simular pagamento"
                                                                }
                                                                target.innerText = "OK"
                                                                target.disabled = false
                                                            } catch (e: Throwable) {
                                                                console.error(
                                                                    "Falha ao simular pagamento",
                                                                    e
                                                                )
                                                                target.innerText = "FALHOU"
                                                                target.disabled = false
                                                                return@launch
                                                            }
                                                        }
                                                    }
                                                    +"Simular Pagamento (Sandbox ${gateway.readableName})"
                                                }
                                            }
                                        }
                                        p {
                                            small {
                                                em {
                                                    +"Pedido: $orderId"
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
                div("col") {
                    InvoiceDetailsComponent {
                        this.orderId = orderId
                        this.paymentMethod = openPayment.gateway
                    }
                }
            }
        }
    }
}
